import { plural, t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { type SourcingAvailability, SourcingAvailabilityStatus } from '@luminovo/api-contracts';
import { isPresent } from '@luminovo/commons';
import { type AvailabilityRenderOption, formatAvailabilityDuration } from '@luminovo/data-stack';
import { colorSystem, Flexbox, InfoFilledIcon, StatusChip, Text, Tooltip } from '@luminovo/design-system';

function statusToColor(status: SourcingAvailabilityStatus): keyof typeof colorSystem {
    switch (status) {
        case SourcingAvailabilityStatus.InStock:
            return 'green';
        case SourcingAvailabilityStatus.LeadTime:
        case SourcingAvailabilityStatus.OnOrder:
            return 'yellow';
        case SourcingAvailabilityStatus.OnOrderUnknownLeadTime:
        case SourcingAvailabilityStatus.NotSpecified:
            return 'red';
    }
}

function getChipLabel(availability: SourcingAvailability, renderOption: AvailabilityRenderOption): string {
    switch (availability.status) {
        case SourcingAvailabilityStatus.InStock:
            if (isPresent(availability.shipping_time_in_days) && availability.shipping_time_in_days > 0) {
                return formatAvailabilityDuration(availability.delivery_time_in_days, renderOption);
            }
            return t`In stock`;

        case SourcingAvailabilityStatus.LeadTime:
        case SourcingAvailabilityStatus.OnOrder:
            return formatAvailabilityDuration(availability.delivery_time_in_days, renderOption);

        case SourcingAvailabilityStatus.OnOrderUnknownLeadTime:
            return t`On order`;

        case SourcingAvailabilityStatus.NotSpecified:
            return t`Not specified`;
    }
}

function formatExactWeeksAndDays(days: number | null): string {
    if (!isPresent(days)) {
        return '-';
    }

    const weeks = Math.floor(days / 7);
    const remainingDays = days % 7;

    if (weeks > 0 && remainingDays > 0) {
        const weeksPart = `${weeks} ${plural(weeks, { one: 'week', other: 'weeks' })}`;
        const daysPart = `${remainingDays} ${plural(remainingDays, { one: 'day', other: 'days' })}`;
        return `${weeksPart} ${daysPart}`;
    }

    if (weeks > 0) {
        return `${weeks} ${plural(weeks, { one: 'week', other: 'weeks' })}`;
    }

    return `${remainingDays} ${plural(remainingDays, { one: 'day', other: 'days' })}`;
}

function formatLeadTimeLabel(availability: SourcingAvailability): string {
    switch (availability.status) {
        case SourcingAvailabilityStatus.InStock:
            return t`In stock`;
        case SourcingAvailabilityStatus.OnOrderUnknownLeadTime:
            return t`Unknown`;
        case SourcingAvailabilityStatus.NotSpecified:
            return t`Not specified`;
        case SourcingAvailabilityStatus.LeadTime:
        case SourcingAvailabilityStatus.OnOrder:
            return formatExactWeeksAndDays(availability.lead_time_in_days);
    }
}

function TooltipRow({ label, value }: { label: React.ReactNode; value: string }): JSX.Element {
    return (
        <Flexbox
            gap="16px"
            sx={{
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
            }}
        >
            <Text variant="body-small-semibold" color={colorSystem.neutral[8]} style={{ whiteSpace: 'nowrap' }}>
                {label}
            </Text>
            <Text
                variant="body-small"
                color={colorSystem.neutral[9]}
                style={{ whiteSpace: 'nowrap', textAlign: 'right', flex: 1 }}
            >
                {value}
            </Text>
        </Flexbox>
    );
}

function DeliveryTimeLabel({ availability }: { availability: SourcingAvailability }): JSX.Element {
    const tooltipTitle =
        availability.shipping_time_info?.source === 'OfferDelivery'
            ? t`The delivery time is the lead time plus the delivery time from the offer.`
            : t`The delivery time is the lead time plus the shipping time. The shipping time is configured on the supplier.`;

    return (
        <Flexbox
            gap="4px"
            style={{ whiteSpace: 'nowrap' }}
            sx={{
                alignItems: 'center',
            }}
        >
            <Text variant="body-small-semibold" color={colorSystem.neutral[8]}>
                <Trans>Delivery time</Trans>
            </Text>
            <Tooltip title={tooltipTitle} variant="dark" arrow>
                <InfoFilledIcon size={16} color={colorSystem.neutral[5]} aria-hidden />
            </Tooltip>
        </Flexbox>
    );
}

function TooltipContent({ availability }: { availability: SourcingAvailability }): JSX.Element {
    const leadTimeLabel = formatLeadTimeLabel(availability);
    const shippingDurationLabel = formatExactWeeksAndDays(availability.shipping_time_in_days);
    const shippingLabel =
        availability.shipping_time_info?.label && isPresent(availability.shipping_time_in_days)
            ? `${shippingDurationLabel} ${availability.shipping_time_info.label}`
            : shippingDurationLabel;
    const shippingRowLabel =
        availability.shipping_time_info?.source === 'OfferDelivery' ? (
            <Trans>Delivery</Trans>
        ) : (
            <Trans>Shipping time</Trans>
        );

    const deliveryLabel =
        availability.status === SourcingAvailabilityStatus.OnOrderUnknownLeadTime
            ? t`Unknown`
            : availability.status === SourcingAvailabilityStatus.NotSpecified
              ? t`Not specified`
              : formatExactWeeksAndDays(availability.delivery_time_in_days);

    return (
        <Flexbox
            gap="8px"
            sx={{
                flexDirection: 'column',
                padding: '4px',
                minWidth: '200px',
            }}
        >
            <TooltipRow label={<Trans>Lead time</Trans>} value={leadTimeLabel} />
            <TooltipRow label={shippingRowLabel} value={shippingLabel} />
            <Flexbox
                gap="16px"
                sx={{
                    borderTop: `1px solid ${colorSystem.neutral[2]}`,
                    paddingTop: '8px',
                    width: '100%',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <DeliveryTimeLabel availability={availability} />
                <Text
                    variant="body-small"
                    color={colorSystem.neutral[9]}
                    style={{ whiteSpace: 'nowrap', textAlign: 'right', flex: 1 }}
                >
                    {deliveryLabel}
                </Text>
            </Flexbox>
        </Flexbox>
    );
}

export function SourcingAvailabilityChip({
    availability,
    renderOption = 'days',
}: {
    availability: SourcingAvailability;
    renderOption?: AvailabilityRenderOption;
}): JSX.Element {
    return (
        <Tooltip
            title={<TooltipContent availability={availability} />}
            variant="white"
            disableInteractive={false}
            disableMaxWidth
        >
            <span style={{ display: 'inline-flex' }}>
                <StatusChip
                    color={statusToColor(availability.status)}
                    label={getChipLabel(availability, renderOption)}
                />
            </span>
        </Tooltip>
    );
}
